/**
 * MANAGEMENT (14) / Support WORKFLOW — pure helpers for the workflow enhancements, kept out of a screen file
 * so the triage panel + SLA strip share them without tripping the react-refresh single-export rule.
 *
 * The status-transition graph MIRRORS the server state machine in supportWorkflow.service.js (TRANSITIONS).
 * The client uses it ONLY to shape the status Select (offer just the legal next edges) — the server stays
 * the authority and 409s any illegal edge regardless of what the UI offered (never a trust boundary).
 */
import type { BadgeTone } from "@/ui";
import type { TicketStatut } from "./types";
import type { SlaState } from "./supportWorkflow.types";

/**
 * The allowed status-transition graph (mirrors the server TRANSITIONS). A self-transition is always allowed
 * (idempotent no-op) and is folded into {@link allowedNextStatuts}.
 *   ouvert   → en_cours | resolu | ferme
 *   en_cours → ouvert   | resolu | ferme
 *   resolu   → en_cours | ouvert | ferme
 *   ferme    → ouvert   | en_cours          (a closed ticket may only be REOPENED)
 */
const TRANSITIONS: Record<TicketStatut, ReadonlyArray<TicketStatut>> = {
  ouvert: ["en_cours", "resolu", "ferme"],
  en_cours: ["ouvert", "resolu", "ferme"],
  resolu: ["en_cours", "ouvert", "ferme"],
  ferme: ["ouvert", "en_cours"],
};

/**
 * The statuts a ticket may move to from `from`, INCLUDING `from` itself (so the Select can render the
 * current statut as the selected option). Returns a stable order: the current statut first, then its legal
 * next edges in graph order.
 */
export function allowedNextStatuts(from: TicketStatut): ReadonlyArray<TicketStatut> {
  return [from, ...TRANSITIONS[from]];
}

/** Is `to` a legal next statut from `from`? (self-transition always allowed — mirrors the server). */
export function isAllowedTransition(from: TicketStatut, to: TicketStatut): boolean {
  return from === to || TRANSITIONS[from].includes(to);
}

/**
 * Badge tone for an SLA overdue magnitude, so the breach reads by severity (never color-only — text + the
 * hours accompany it). Deeper overdue → hotter: > target ⇒ danger; a mild breach ⇒ warn.
 */
export function slaOverdueTone(sla: SlaState): BadgeTone {
  if (!sla.breached) return "neutral";
  // Past DOUBLE the target = a hard breach (danger); otherwise a warning.
  return sla.overdueHours >= sla.slaTargetHours ? "danger" : "warn";
}
